import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorComponent } from '../../core';
import { BusinessObject, Product, ProductCategory } from '../../models';
import { BusinessObjectUtils } from '../../utils';
import { BreakpointService, DialogService } from '../../services';
import {
  ProductPromotionDialogComponent,
  ProductPromotionDialogComponentData,
} from './product-promotion-dialog/product-promotion-dialog.component';
import {
  ProductHappyHourDialogComponent,
  ProductHappyHourDialogComponentData,
} from './product-happy-hour-dialog/product-happy-hour-dialog.component';
import { CommonModule } from '@angular/common';
import { LibCoreLayoutModule } from '../core-layout';
import { PipesModule } from '../../pipes';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRippleModule } from '@angular/material/core';

@Component({
  selector: 'lib-salon-service-selection',
  templateUrl: './salon-service-selection.component.html',
  styleUrls: ['./salon-service-selection.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    PipesModule,
    TranslateModule,
    // material design
    MatButtonModule,
    MatExpansionModule,
    MatRippleModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SalonServiceSelectionComponent),
      multi: true,
    },
  ],
})
export class SalonServiceSelectionComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  private _businessObject: BusinessObject;
  @Input() set businessObject(value: BusinessObject) {
    this._businessObject = value;
    this.updatePreSelectedOrExpandedCategory();
  }
  get businessObject(): BusinessObject {
    return this._businessObject;
  }

  @Input() selectedTime: Date;

  @Input() originalSelectedProductIds: number[];
  @Input() disableSelectedProducts: boolean;

  public isDisabled = false;

  public selectedProductIds: number[];

  // For wide screen mode
  public preSelectedCategoryToDisplayOnWideScreen: ProductCategory;

  // this list should be init only if businessObject changes
  private listOfAutoExpandedCategoryId: number[];

  constructor(
    public breakpointService: BreakpointService,
    private dialogService: DialogService
  ) {
    super();
  }

  public shouldAutoExpandedCategory(category: ProductCategory): boolean {
    return this.listOfAutoExpandedCategoryId.includes(category.id);
  }

  public shouldDisableProductSelection(productId: number): boolean {
    if (!this.disableSelectedProducts) {
      return false;
    }

    return this.originalSelectedProductIds?.includes(productId);
  }

  public isProductSelected(productId: number): boolean {
    return this.selectedProductIds?.includes(productId);
  }

  public productClickHandler(productId: number): void {
    if (this.isDisabled) {
      return;
    }

    if (!this.selectedProductIds?.length) {
      this.selectedProductIds = [productId];
    } else if (this.isProductSelected(productId)) {
      this.selectedProductIds = this.selectedProductIds?.filter(
        (pId) => pId !== productId
      );
    } else {
      this.selectedProductIds = [...this.selectedProductIds, productId];
    }

    this._onChange(this.selectedProductIds);
  }

  public isCategorySelected(categoryId: number): boolean {
    return this.preSelectedCategoryToDisplayOnWideScreen?.id === categoryId;
  }

  public categoryClickHandler(category: ProductCategory): void {
    this.preSelectedCategoryToDisplayOnWideScreen = category;
  }

  public displayDiscountDetails(product: Product): void {
    if (this.shouldDisplayPromotionDetails(product)) {
      this.dialogService.openDialog(
        ProductPromotionDialogComponent,
        this.dialogService.createSmallDialogConfig({
          product,
          currency: this.businessObject?.currency,
        } as ProductPromotionDialogComponentData)
      );
    } else if (this.shouldDisplayHappyHourDetails(product)) {
      this.dialogService.openDialog(
        ProductHappyHourDialogComponent,
        this.dialogService.createSmallDialogConfig({
          product,
          currency: this.businessObject?.currency,
        } as ProductHappyHourDialogComponentData)
      );
    }
  }

  public shouldDisplayDiscountIconForCategory(
    category: ProductCategory
  ): boolean {
    if (
      !BusinessObjectUtils.isContainAnyProductWithActivatedDiscount(category)
    ) {
      return false;
    }

    return BusinessObjectUtils.isContainAnyProductWithActivatedDiscountAtTimeStamp(
      category,
      this.generateSelectedTime()
    );
  }

  public isAnyDiscountActivated(product: Product): boolean {
    return BusinessObjectUtils.isAnyDiscountActivatedAtTimeStamp(
      product,
      this.generateSelectedTime()
    );
  }

  public getPromotionalPrice(product: Product): number {
    return BusinessObjectUtils.getPromotionalPrice(
      product,
      this.generateSelectedTime()
    );
  }

  public shouldDisplayPromotionDetails(product: Product): boolean {
    if (!BusinessObjectUtils.isPromotionActivated(product)) {
      return false;
    }

    if (
      BusinessObjectUtils.isPromotionActivatedAtTimeStamp(
        product,
        this.generateSelectedTime()
      )
    ) {
      return true;
    }

    if (
      BusinessObjectUtils.isHappyHoursActivatedAtTimeStamp(
        product,
        this.generateSelectedTime()
      )
    ) {
      return false;
    }

    if (
      BusinessObjectUtils.isPromotionExpiredAtTimeStamp(
        product,
        this.selectedTime
      )
    ) {
      return false;
    }

    return true;
  }

  public shouldDisplayHappyHourDetails(product: Product): boolean {
    if (!BusinessObjectUtils.isHappyHoursActivated(product)) {
      return false;
    }

    if (
      BusinessObjectUtils.isPromotionActivatedAtTimeStamp(
        product,
        this.generateSelectedTime()
      )
    ) {
      return false;
    }

    if (
      BusinessObjectUtils.isHappyHoursActivatedAtTimeStamp(
        product,
        this.generateSelectedTime()
      )
    ) {
      return true;
    }

    if (
      BusinessObjectUtils.isPromotionExpiredAtTimeStamp(
        product,
        this.selectedTime
      )
    ) {
      return true;
    }

    if (BusinessObjectUtils.isPromotionActivated(product)) {
      return false;
    }

    return true;
  }

  private generateSelectedTime(): Date {
    return this.selectedTime || new Date();
  }

  private updatePreSelectedOrExpandedCategory(): void {
    const listOfAutoExpandedCategoryId: number[] = [];

    this.businessObject?.categories?.forEach((c) => {
      if (this.selectedProductIds?.length) {
        // expand category if contain selected product
        if (
          c?.products?.findIndex((p) =>
            this.selectedProductIds.includes(p.id)
          ) >= 0
        ) {
          listOfAutoExpandedCategoryId.push(c.id);
        }
      }
    });

    if (!listOfAutoExpandedCategoryId?.length) {
      // auto expand first category if there is no selected products
      listOfAutoExpandedCategoryId.push(
        this.businessObject?.categories?.[0]?.id
      );
    }

    this.listOfAutoExpandedCategoryId = listOfAutoExpandedCategoryId;

    if (this.listOfAutoExpandedCategoryId?.length) {
      this.preSelectedCategoryToDisplayOnWideScreen =
        this.businessObject?.categories?.find(
          (c) => c.id === this.listOfAutoExpandedCategoryId[0]
        );
    } else {
      this.preSelectedCategoryToDisplayOnWideScreen = null;
    }
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(value: number[]) {
    this.selectedProductIds = [...(value || [])];
    this.updatePreSelectedOrExpandedCategory();
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
